export const parseSSEData = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const normalizeNewlines = (text) => text.replace(/\r\n/g, "\n");

const splitSseMessages = (buffer) => {
  const normalized = normalizeNewlines(buffer);
  const messages = [];
  let rest = normalized;
  let idx;
  while ((idx = rest.indexOf("\n\n")) >= 0) {
    const block = rest.slice(0, idx).trim();
    rest = rest.slice(idx + 2);
    if (block) messages.push(block);
  }
  return { messages, rest };
};

const parseSseBlock = (block) => {
  let eventName = "message";
  const dataLines = [];
  for (const line of block.split("\n")) {
    if (!line.trim()) continue;
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  return { event: eventName, data: dataLines.join("\n") };
};

/**
 * Call-center SSE: uses fetch + ReadableStream (same as EventSource, cannot rely on
 * Authorization header for auth in all environments). The stream URL must include
 * `access_token` (or `token`); see getCallStreamUrl in CallCenterCalls.
 */
export const createSSEClient = ({
  url,
  onOpen,
  onMessage,
  onError,
  maxRetries = 6,
  initialRetryMs = 1000,
}) => {
  let closedByUser = false;
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let abortController = null;

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = (connectFn) => {
    if (closedByUser) return;
    if (reconnectAttempts >= maxRetries) return;
    const delayMs = Math.min(initialRetryMs * 2 ** reconnectAttempts, 15000);
    reconnectAttempts += 1;
    reconnectTimer = window.setTimeout(connectFn, delayMs);
  };

  const connect = async () => {
    if (closedByUser) return;
    clearReconnectTimer();
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
        },
        credentials: "include",
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE request failed: ${response.status}`);
      }

      reconnectAttempts = 0;
      if (typeof onOpen === "function") onOpen();

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("SSE response has no body");
      }

      const decoder = new TextDecoder();
      let buffer = "";

      while (!closedByUser) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) {
            const { messages, rest } = splitSseMessages(buffer);
            buffer = rest;
            for (const block of messages) {
              const { event, data } = parseSseBlock(block);
              if (event === "call.state") {
                const parsed = parseSSEData(data);
                if (parsed && typeof onMessage === "function") onMessage(parsed);
              }
            }
          }
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const { messages, rest } = splitSseMessages(buffer);
        buffer = rest;
        for (const block of messages) {
          const { event, data } = parseSseBlock(block);
          if (event === "call.state") {
            const parsed = parseSSEData(data);
            if (parsed && typeof onMessage === "function") onMessage(parsed);
          }
        }
      }
    } catch (error) {
      if (closedByUser || error?.name === "AbortError") return;
      if (typeof onError === "function") onError(error);
      scheduleReconnect(connect);
    }
  };

  connect();

  return {
    close: () => {
      closedByUser = true;
      clearReconnectTimer();
      if (abortController) {
        abortController.abort();
      }
    },
  };
};
